import React from "react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

/**
 * Flight / Train / Bus number input — shared between AddSegmentDialog
 * and EditSegmentDialog so the label, placeholder, and (optional)
 * "Check Status" button stay in lockstep.
 *
 * Renders nothing for segment types that don't carry a service number
 * (accommodation, cruise, activity, etc.) so the caller doesn't have
 * to repeat the `(formData.type === 'flight' || ...)` guard.
 *
 * Props:
 *   value                — current `formData.flight_number`.
 *   segmentType          — current `formData.type`. Drives the label
 *                          ("Flight Number" / "Train/Service Number" /
 *                          "Route Number") and the placeholder.
 *   onChange             — (newValue: string) => void.
 *   onCheckStatus        — optional click handler for the "Check Status"
 *                          button. Button hidden when not provided.
 *   checking             — when truthy renders the button as
 *                          "Checking..." and disabled.
 *   testIdPrefix         — 'segment' (Add) or 'edit-segment' (Edit).
 */
const TYPE_TO_LABEL = {
  flight: "Flight Number",
  train: "Train/Service Number",
  bus: "Route Number",
};
const TYPE_TO_PLACEHOLDER = {
  flight: "e.g., DL123",
  train: "e.g., Nozomi 225",
  bus: "e.g., Route 42",
};

export const SegmentFlightNumberField = ({
  value,
  segmentType,
  onChange,
  onCheckStatus,
  checking = false,
  testIdPrefix = "segment",
}) => {
  if (!["flight", "train", "bus"].includes(segmentType)) return null;
  const label = TYPE_TO_LABEL[segmentType];
  const placeholder = TYPE_TO_PLACEHOLDER[segmentType];
  const showStatusButton =
    segmentType === "flight" && Boolean(value) && typeof onCheckStatus === "function";
  const id = `${testIdPrefix}-flight-number`;

  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2">
        <Input
          id={id}
          data-testid={`${testIdPrefix}-flight-number-input`}
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1"
        />
        {showStatusButton && (
          <Button
            type="button"
            variant="outline"
            onClick={onCheckStatus}
            disabled={checking}
            data-testid={`${testIdPrefix}-flight-status-button`}
          >
            {checking ? "Checking..." : "Check Status"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default SegmentFlightNumberField;
